// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { components } from "@/api/schema";

type VendorsSortBy = components["schemas"]["VendorsSortByEnum"];
type SortOrder = components["schemas"]["HttpSortingOrderEnum"];

export interface VendorRow {
  vendorId: number;
  name?: string;
  apiKey?: string;
  chainName?: string;
  isActive: boolean;
  createdAt?: string;
}

interface VendorsListParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: VendorsSortBy;
  orderBy?: SortOrder;
  onlyActive?: boolean;
  chainName?: string;
}

export function useVendorsQuery(params: VendorsListParams = {}) {
  const { page = 1, limit = 20, search, sortBy, orderBy, onlyActive, chainName } = params;

  return useQuery({
    queryKey: ["vendors", "list", { page, limit, search, sortBy, orderBy, onlyActive, chainName }],
    queryFn: async () => {
      const res = await api.GET("/v1/vendors", {
        params: {
          query: {
            page,
            limit,
            search: search || undefined,
            sortBy,
            orderBy,
            "filter[onlyActive]": onlyActive,
            "filter[chainName]": chainName || undefined,
          },
        },
      });
      return res.data as unknown as {
        vendors: VendorRow[];
        count: number;
      };
    },
  });
}

type VendorUsageSortBy = components["schemas"]["VendorUsageSortByEnum"];

export interface VendorUsageRow {
  usageType: "ingress" | "export";
  id?: number;
  type?: string;
  storeId?: number;
  storeCode?: string;
  chainName?: string;
  status?: string;
  source?: string;
  createdAt?: string;
}

export interface VendorUsageStore {
  storeId: number;
  storeName: string;
  legibleName: string;
}

interface VendorUsageParams {
  vendorName: string;
  page?: number;
  limit?: number;
  sortBy?: VendorUsageSortBy;
  orderBy?: SortOrder;
}

export function useVendorUsageQuery({ vendorName, page = 1, limit = 20, sortBy, orderBy }: VendorUsageParams) {
  return useQuery({
    queryKey: ["vendors", "usage", vendorName, { page, limit, sortBy, orderBy }],
    queryFn: async () => {
      const res = await api.GET("/v1/vendors/{name}/usage", {
        params: { path: { name: vendorName }, query: { page, limit, sortBy, orderBy } },
      });
      if (res.error) throw new Error("Failed to load vendor usage");
      return res.data as unknown as { usage: VendorUsageRow[]; stores: VendorUsageStore[]; count: number };
    },
    enabled: !!vendorName,
  });
}

export function useVendorByNameQuery(name: string) {
  return useQuery({
    queryKey: ["vendors", "detail", name],
    queryFn: async () => {
      const res = await api.GET("/v1/vendors/{name}", {
        params: { path: { name } },
      });
      if (res.error) throw new Error("Failed to load vendor");
      return res.data as unknown as VendorRow;
    },
    enabled: !!name,
  });
}

// --- Create ---

export interface CreateVendorInput {
  name: string;
  chainName: string;
}

export interface CreateVendorResult {
  apiKey: string;
  privateKey: string;
}

export function useCreateVendorMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateVendorInput) => {
      const res = await api.POST("/v1/vendors", {
        body: { name: input.name, chainName: input.chainName },
      });
      if (res.error) throw new Error("Failed to create vendor");
      return res.data as unknown as CreateVendorResult;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendors"] });
    },
  });
}

// --- Activate / Deactivate ---

export function useToggleVendorActiveMutation(name: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (isActive: boolean) => {
      const res = await api.PATCH("/v1/vendors/{name}/active", {
        params: { path: { name } },
        body: { isActive },
      });
      if (res.error) throw new Error("Failed to update");
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendors"] });
    },
  });
}
